<template>
  <div class="full-height">
    <a-spin :spinning="isLoading">
      <div class="chart-container">
        <div class="header">
          <div class="chart-title">
            {{ $tm('statistic.departmentsChart.title') }}
          </div>
        </div>
        <EmptyChart v-if="option.series[0].data.length === 0" />
        <v-chart
          v-else
          class="chart"
          :option="option"
          autoresize
        />
      </div>
    </a-spin>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { use } from 'echarts/core'
import { SVGRenderer } from 'echarts/renderers'
import { BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'

import * as color from '@/constants/statistic-color'
import { getDepartmentsRates } from '@/graphql/statistic'
import EmptyChart from '@/components/EmptyChart'

use([
  SVGRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
])

const rateNameArr = [
  '填写率',
  '对齐率',
  '更新率',
  '复盘率'
]

const ratePropArr = [
  'okrPercent',
  'linkPercent',
  'updatePercent',
  'reviewPercent'
]

export default defineComponent({
  name: 'DepartmentsChart',
  components: {
    VChart,
    EmptyChart
  },
  provide: {
    [THEME_KEY]: 'light'
  },
  props: {
    departmentIds: {
      type: Array,
      required: true
    }
  },
  emits: [
    'update:is-chart-full-width'
  ],
  setup (props) {
    const { state } = useStore()
    const { tm } = useI18n()
    const cycleId = computed(() => state.selectedCycleId)
    const lang = computed(() => state.app.lang)
    const isLoading = ref(false)
    let seriesData = new Array(4).fill(0).map(() => [])

    const labelOption = {
      show: true,
      position: 'insideBottom',
      distance: 15,
      align: 'left',
      verticalAlign: 'middle',
      rotate: 90,
      formatter: '{a} {c}%',
      fontSize: 10
    }

    const option = reactive({
      grid: {
        bottom: 120
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      color: [
        color.filledIn.from,
        color.aligned.from,
        color.updated.from,
        color.reviewed.from
      ],
      legend: {
        data: rateNameArr.map(rateName => departmentsChartTm(rateName))
      },
      xAxis: [
        {
          type: 'category',
          axisTick: {
            show: false
          },
          axisLabel: {
            show: true,
            interval: 0,
            rotate: 30,
            color: 'rgba(0, 0, 0, .88)'
          },
          axisLine: {
            lineStyle: {
              color: '#e3e3e3'
            }
          },
          data: []
        }
      ],
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: '{value} %'
          }
        }
      ],
      series: getSeries()
    })

    loadData()

    watch([
      cycleId,
      () => props.departmentIds
    ], loadData)

    watch(lang, () => {
      option.legend.data = rateNameArr.map(item => departmentsChartTm(item))
      option.series = getSeries()
    })

    function loadData () {
      if (cycleId.value === null || props.departmentIds.length === 0) {
        return
      }
      isLoading.value = true
      getDepartmentsRates(cycleId.value, props.departmentIds)
        .then(departments => {
          const xAxisData = []
          seriesData = new Array(4).fill(0).map(() => [])
          departments.forEach(department => {
            xAxisData.push(department.deptName.split('-').pop()) // X 轴标签
            ratePropArr.forEach((rateProp, index) =>
              seriesData[index].push(Math.round(department[rateProp] * 100) / 100)
            )
          })

          option.xAxis[0].data = xAxisData
          ratePropArr.forEach((rateProp, index) => {
            option.series[index].data = seriesData[index]
          })
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    function getSeries () {
      return rateNameArr.map((rateName, index) => ({
        name: departmentsChartTm(rateName),
        type: 'bar',
        barGap: 0,
        label: labelOption,
        emphasis: {
          focus: 'series'
        },
        barMaxWidth: 20,
        itemStyle: {
          borderRadius: [8, 8, 0, 0]
        },
        data: seriesData[index]
      }))
    }

    function departmentsChartTm (name) {
      return tm('statistic.departmentsChart.' + name)
    }

    return {
      isLoading,
      option
    }
  }
})
</script>

<style lang="less" scoped>
.full-height {
  height: calc(100vh - 300px);
  min-height: 600px;

  :deep(.ant-spin-nested-loading) {
    height: 100%;
  }

  :deep(.ant-spin-container) {
    height: 100%;
  }
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chart {
  height: 100%;
  width: 100%;
}
</style>
